import type { ReactElement } from "react";
import { Link } from "react-router";

import type { FieldErrors } from "../../api/client.js";
import { describedBy, errorIdOf } from "../../forms/described-by.js";
import { FormSummary } from "../../forms/fields.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import { adminCenterUrls } from "./urls.js";

/** 探す条件（サーバの応答の条件の欄。<b>期間の既定はサーバが決める</b>——今日までの 1 か月）。 */
export interface PaymentConditions {
    /** yyyy-MM-dd。 */
    readonly from: string | null;
    readonly to: string | null;
    /** 結果（<c>succeeded</c>・<c>failed</c>）。null ならすべて。 */
    readonly result: string | null;
    readonly keyword: string | null;
    /** 契約 ID（<c>PayLogs.TenantId</c>）。契約 1 件の画面から渡ってくる。 */
    readonly tenantId: number | null;
}

/** 結果の選択肢（並びと文言はサーバが決める。「すべて」は画面が足す）。 */
export interface PaymentResultChoice {
    readonly value: string;
    readonly label: string;
}

export const text = (value: number | string | null): string => (value === null ? "" : String(value));

/** 欄の断り。<b>名前の大文字・小文字は問わない</b>（サーバの欄の名前の書き方に依らない）。 */
export const errorOf = (errors: FieldErrors, name: string): string | undefined =>
    Object.entries(errors).find(([key]) => key.toLowerCase() === name)?.[1][0];

/** 欄の下に出さない断り（期間の欄以外）。先頭にまとめて出す。 */
export const summaryOf = (errors: FieldErrors): string[] => Object.entries(errors)
    .filter(([key]) => !["from", "to"].includes(key.toLowerCase()))
    .flatMap(([, messages]) => messages);

export function DateField({ name, label, value, error }: {
    readonly name: string;
    readonly label: string;
    readonly value: string;
    readonly error: string | undefined;
}): ReactElement {
    const invalid = error !== undefined;
    return (
        <div>
            <label htmlFor={name} className="form-label">{label}</label>
            <input
                id={name} name={name} type="date" defaultValue={value}
                className={invalid ? "form-input is-invalid" : "form-input"}
                aria-invalid={invalid ? true : undefined}
                {...describedBy(name, { help: false, invalid, required: false })}
            />
            {invalid ? <span className="form-error field-validation-error" id={errorIdOf(name)}>{error}</span> : null}
        </div>
    );
}

/**
 * 課金の結果を探す窓。<b>Enter で探してもフォーカスとスクロールを保つ</b>（<c>useSearchForm</c>。docs/01 §8）。
 * 探し直すと 1 ページ目に戻る（ページはクエリに載せない）。
 * <b>契約 ID は欄に入れて保つ</b>（アクティビティログの窓と同じ）——ほかの条件を変えても絞ったまま。外すのは欄を空にするか「クリア」。
 */
export function PaymentSearchForm({ conditions, results, errors }: {
    readonly conditions: PaymentConditions;
    readonly results: readonly PaymentResultChoice[];
    readonly errors: FieldErrors;
}): ReactElement {
    const form = useSearchForm(adminCenterUrls.payments(), {
        from: text(conditions.from), to: text(conditions.to), result: text(conditions.result), keyword: text(conditions.keyword),
        tenantId: text(conditions.tenantId),
    });

    return (
        <section className="card mb-4 p-6">
            <form method="get" {...form.props}>
                <FormSummary messages={summaryOf(errors)} />
                <div className="mb-3">
                    <label htmlFor="keyword" className="form-label">契約・操作した人で探す</label>
                    <input
                        id="keyword" name="keyword" className="form-input" autoComplete="off"
                        defaultValue={text(conditions.keyword)} placeholder="システム名・企業名・メールアドレスの一部"
                    />
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <DateField name="from" label="この日から" value={text(conditions.from)} error={errorOf(errors, "from")} />
                    <DateField name="to" label="この日まで" value={text(conditions.to)} error={errorOf(errors, "to")} />
                    <div>
                        <label htmlFor="result" className="form-label">結果</label>
                        <select id="result" name="result" className="form-input" defaultValue={text(conditions.result)}>
                            <option value="">すべて</option>
                            {results.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="tenantId" className="form-label">契約 ID</label>
                        <input
                            id="tenantId" name="tenantId" type="number" className="form-input" autoComplete="off"
                            defaultValue={text(conditions.tenantId)}
                        />
                    </div>
                    {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary">検索</button>
                        {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                        <Link to={adminCenterUrls.payments()} className="btn-secondary">クリア</Link>
                    </div>
                </div>
                <p className="form-help">
                    <strong className="text-ink-800">期間の既定は今日までの 1 か月</strong>です。新しい記録から順に並べます。
                </p>
            </form>
        </section>
    );
}
